import { useGetFrontRichiesteIdModuliId } from '@framework/api-client/api/inquiries-compile/richieste-controller/richieste-controller.ts';
import { getNetworkErrorMessage } from '@framework/auth/http/errors/getNetworkErrorMessage.ts';
import { TbIcon } from '@framework/ui/components/icon/TbIcon.tsx';
import { ModalDialog } from '@framework/ui/components/modal/ModalDialog.tsx';
import { CopyableText } from '@framework/ui/components/text/CopyableText.tsx';
import { IconBraces } from '@tabler/icons-react';
import {
  Alert,
  Button,
  type ButtonProps,
  Empty,
  Flex,
  Spin,
  Tooltip,
} from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useSummaryAdditionalData } from '../SummaryAdditionalDataContext.tsx';

// Area scorrevole del JSON: è una `section` con `aria-label` invece di un
// `role="region"`, che non è supportato ovunque.
const JsonBlock = styled.section`
  max-height: 60vh;
  overflow: auto;
  background: ${props => props.theme.colorFillQuaternary};
  border: 1px solid ${props => props.theme.colorBorderSecondary};
  border-radius: 8px;
`;

// `styled.pre` non è disponibile: styled-components è ristretto a un
// sottoinsieme di elementi (packages/ui/styled-components.d.ts). Si usa un
// div stilizzato e si rende l'elemento semantico con `as="pre"`.
const JsonContent = styled.div`
  margin: 0;
  padding: 12px;
  white-space: pre;
  font-family: ${props => props.theme.fontFamilyCode || 'monospace'};
  font-size: 13px;
`;

export interface IModuleJsonModalButtonProps
  extends Omit<ButtonProps, 'onClick'> {
  submissionId: string;
  moduleId: string;
  /**
   * Istanza di procedimento della richiesta. Obbligatorio nel Backoffice:
   * senza questo parametro il BE risponde 403 all'operatore, che non ha le
   * abilitazioni del canale Frontoffice sulla singola richiesta.
   */
  procedureId?: string;
}

/**
 * Azione "Visualizza in formato JSON" sui dati di compilazione di un modulo.
 *
 * Compare solo dove il context dei dati aggiuntivi è montato (Backoffice) e
 * il modulo espone `jsonDisponibile`. I valori si caricano all'apertura
 * della modale, non al render della riga: il payload di un modulo compilato
 * è pesante e la maggior parte delle righe non viene mai aperta.
 */
export function ModuleJsonModalButton(
  props: Readonly<IModuleJsonModalButtonProps>,
) {
  const { submissionId, moduleId, procedureId, ...buttonProps } = props;
  const [modalOpen, setModalOpen] = useState(false);
  const { t } = useTranslation();
  const additionalData = useSummaryAdditionalData();

  const { data, isFetching, isSuccess, error } = useGetFrontRichiesteIdModuliId(
    submissionId,
    moduleId,
    { idIstanzaProcedimento: procedureId },
    { query: { enabled: modalOpen } },
  );

  if (additionalData?.modulesById.get(moduleId)?.jsonDisponibile !== true) {
    return null;
  }

  const label = t('submission-summary.visualizza-json', 'Visualizza JSON');
  const jsonString = data?.valoriSezioni
    ? JSON.stringify(data.valoriSezioni, null, 2)
    : null;

  return (
    <>
      <Tooltip title={label}>
        <Button
          icon={<TbIcon icon={IconBraces} />}
          aria-label={label}
          type="link"
          size="middle"
          {...buttonProps}
          onClick={() => setModalOpen(true)}
        />
      </Tooltip>

      <ModalDialog
        open={modalOpen}
        title={label}
        onCancel={() => setModalOpen(false)}
        size="medium"
        footer={
          jsonString ? (
            <CopyableText
              copyable={{
                text: jsonString,
                tooltips: [
                  t('submission-summary.copia-json', 'Copia JSON'),
                  t('submission-summary.json-copiato', 'JSON copiato!'),
                ],
              }}
            >
              {t('submission-summary.copia-json', 'Copia JSON')}
            </CopyableText>
          ) : null
        }
        destroyOnClose
      >
        {isFetching && (
          <Flex justify="center" style={{ padding: 24 }}>
            <Spin />
          </Flex>
        )}
        {!isFetching && error != null && (
          <Alert
            type="error"
            showIcon
            message={getNetworkErrorMessage(
              error,
              t(
                'submission-summary.modulo-json-errore',
                'Si è verificato un problema durante il caricamento dei dati del modulo',
              ),
            )}
          />
        )}
        {!isFetching && isSuccess && !jsonString && (
          <Empty
            description={t(
              'submission-summary.modulo-json-vuoto',
              'Il modulo non contiene dati di compilazione',
            )}
          />
        )}
        {!isFetching && jsonString && (
          <JsonBlock
            aria-label={label}
            tabIndex={0} // NOSONAR: Permette di dare il focus al blocco, in modo da poterlo scorrere con la tastiera. ([a11y])
          >
            <JsonContent as="pre">{jsonString}</JsonContent>
          </JsonBlock>
        )}
      </ModalDialog>
    </>
  );
}
